import type { ReactNode } from 'react';

import { Spinner } from '@/components/ui/spinner';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTablePagination } from '@/components/ui/data-table-pagination';
import { Typography } from '@/components/ui/typography';
import type { RowSelection } from '@/components/ui/hooks/use-row-selection';
import type { PropertyTable } from '../metadata/propertyConfig';

type PaginationProps = {
  totalRows: number;
  /** 1-based page the footer opens on; see DataTablePagination. */
  initialPage?: number;
  initialRowsPerPage?: number;
  onPageChange: (
    upperIndex: number,
    lowerIndex: number,
    activeIndex: number,
    rowsPerPage: number
  ) => void;
};

type Props = {
  items: PropertyTable[];
  selection: RowSelection;
  isLoading: boolean;
  showCheckboxes: boolean;
  totalRowsTitle: string;
  totalAvailableRows: number;
  actionBar: ReactNode;
  paginationProps: PaginationProps;
  renderCard: (item: PropertyTable) => ReactNode;
};

export const CardGrid = ({
  items,
  selection,
  isLoading,
  showCheckboxes,
  totalRowsTitle,
  totalAvailableRows,
  actionBar,
  paginationProps,
  renderCard
}: Props) => {
  const masterCheckboxState = selection.isIndeterminate
    ? 'indeterminate'
    : selection.isAllSelected;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {(showCheckboxes || actionBar) && (
        <div className="shrink-0 bg-background flex items-center gap-4 py-2 px-3 border-y border-border">
          {showCheckboxes && (
            <>
              <Checkbox
                checked={masterCheckboxState}
                disabled={isLoading || items.length === 0}
                onCheckedChange={(c) => selection.toggleAll(c === true)}
              />
              <div className="flex flex-col">
                <Typography as="p" variant="body-medium">
                  {totalAvailableRows} {totalRowsTitle}
                </Typography>
                <Typography as="p" variant="small">
                  {selection.selectedIds.size} Selected
                </Typography>
              </div>
            </>
          )}
          {actionBar && <div className="flex-1">{actionBar}</div>}
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <Spinner size="lg" />
          </div>
        )}
        {!isLoading && items.length === 0 && (
          <div className="flex items-center justify-center py-16">
            <Typography className="text-lg">No rows to show</Typography>
          </div>
        )}
        {!isLoading && items.length > 0 && (
          <div className="grid gap-4 p-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {items.map((item) => (
              <div key={item.id}>{renderCard(item)}</div>
            ))}
          </div>
        )}
      </div>
      {!isLoading && items.length > 0 && (
        <div className="shrink-0 border-t border-border bg-background">
          <DataTablePagination
            totalRows={paginationProps.totalRows}
            initialPage={paginationProps.initialPage}
            initialRowsPerPage={paginationProps.initialRowsPerPage}
            onPageChange={paginationProps.onPageChange}
          />
        </div>
      )}
    </div>
  );
};
